import React, { useEffect, useMemo, useState } from 'react';
import {
  Upload,
  X
} from 'lucide-react';

const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

const PrizeEditor = ({
  prize,
  campaignId,
  api,
  onPrizeUpdated,
  setSuccessMessage,
  setShowSuccess,
  setErrorMessage,
  setShowError,
  onCancel,
  variant = 'embedded',
  className = ''
}) => {
  const [formPrize, setFormPrize] = useState(prize ? { ...prize } : null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    setFormPrize(prize ? { ...prize } : null);
  }, [prize]);

  const showSuccess = (message) => {
    if (!setSuccessMessage || !setShowSuccess) return;
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const showError = (message) => {
    if (!setErrorMessage || !setShowError) return;
    setErrorMessage(message);
    setShowError(true);
    setTimeout(() => setShowError(false), 3000);
  };

  const updatePrizeField = (field, value) => {
    setFormPrize((current) => (current ? { ...current, [field]: value } : current));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formPrize) return;

    setSaving(true);
    try {
      const { data } = await api.put(
        `/admin/campaigns/${campaignId}/instant-win/prizes/${formPrize.id}`,
        formPrize
      );

      setFormPrize({ ...data.prize });
      if (typeof onPrizeUpdated === 'function') {
        onPrizeUpdated(data.prize);
      }
      showSuccess('Prize updated successfully!');
    } catch (error) {
      console.error('Failed to update prize:', error);
      showError(error.response?.data?.error || error.message || 'Failed to update prize');
    } finally {
      setSaving(false);
    }
  };

  const handlePrizeLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !formPrize) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(
        `/admin/campaigns/${campaignId}/instant-win/prizes/${formPrize.id}/logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const updatedPrize = { ...formPrize, logo_url: response.data.url };
      setFormPrize(updatedPrize);
      if (typeof onPrizeUpdated === 'function') {
        onPrizeUpdated(updatedPrize);
      }
      showSuccess('Prize logo uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload prize logo:', error);
      showError(error.response?.data?.error || error.message || 'Failed to upload prize logo');
    } finally {
      setUploadingLogo(false);
      event.target.value = '';
    }
  };

  const metrics = useMemo(() => {
    if (!formPrize) {
      return {
        totalQuantity: 0,
        remainingQuantity: 0,
        claimedCount: 0,
        percentRemaining: 0,
        percentRemainingDisplay: 0
      };
    }
    const totalQuantity = Number(formPrize.total_quantity) || 0;
    const remainingQuantity =
      typeof formPrize.remaining_quantity === 'number'
        ? formPrize.remaining_quantity
        : totalQuantity;
    const claimedCount = Math.max(totalQuantity - remainingQuantity, 0);
    const percentRemaining =
      totalQuantity > 0 ? Math.min(100, Math.max(0, (remainingQuantity / totalQuantity) * 100)) : 0;
    const percentRemainingDisplay = Math.round(percentRemaining);

    return {
      totalQuantity,
      remainingQuantity,
      claimedCount,
      percentRemaining,
      percentRemainingDisplay
    };
  }, [formPrize]);

  if (!formPrize) {
    return null;
  }

  const containerClasses = [
    'bg-white rounded-3xl shadow-2xl p-6 md:p-8 space-y-8',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <form onSubmit={handleSubmit} className="space-y-8">
        <section className="space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-500">
            Prize details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Prize Name*
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                value={formPrize.prize_name || ''}
                onChange={(event) => updatePrizeField('prize_name', event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Prize Value ($)*
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                value={formPrize.prize_value || ''}
                onChange={(event) => updatePrizeField('prize_value', event.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Total Quantity*
              </label>
              <input
                type="number"
                required
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                value={formPrize.total_quantity || ''}
                onChange={(event) => updatePrizeField('total_quantity', event.target.value)}
              />
            </div>
            <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 text-sm">
              <p className="text-teal-900">
                <span className="font-semibold">Claimed:</span>{' '}
                {metrics.claimedCount.toLocaleString()}
              </p>
              <p className="text-teal-700 mt-1">
                <span className="font-semibold">Remaining:</span>{' '}
                {metrics.remainingQuantity.toLocaleString()}
              </p>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-500">
            Visual identity
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <div className="border border-gray-200 rounded-2xl p-4 bg-gray-50 flex flex-col items-center justify-center">
                <div className="w-32 h-32 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
                  {formPrize.logo_url ? (
                    <img
                      src={`${ASSET_BASE_URL}${formPrize.logo_url}`}
                      alt={`${formPrize.prize_name} logo`}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-gray-400 text-sm">No logo uploaded</span>
                  )}
                </div>
                {formPrize.logo_url && (
                  <button
                    type="button"
                    onClick={() => updatePrizeField('logo_url', '')}
                    className="mt-3 text-sm text-red-600 hover:text-red-700"
                  >
                    Remove Logo
                  </button>
                )}
              </div>
            </div>
            <div className="md:col-span-2 space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Upload New Logo
              </label>
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                <Upload className="w-5 h-5" />
                <span>Choose file</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePrizeLogoUpload}
                  disabled={uploadingLogo}
                  className="hidden"
                />
              </label>
              {uploadingLogo ? (
                <p className="text-xs text-gray-500">Uploading logo…</p>
              ) : (
                <p className="text-xs text-gray-500">
                  Use a square PNG or SVG for best results. Transparent backgrounds look great here.
                </p>
              )}
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          {typeof onCancel === 'function' && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PrizeEditor;
